/* Web Push opt-in helpers.
 *
 * The push subscription dance:
 *   1. Register the service worker at /sw.js
 *   2. Ask the OS for notification permission (a one-time prompt)
 *   3. Call PushManager.subscribe with the server's VAPID public key
 *   4. POST the resulting subscription JSON back to the server
 *
 * The server stores the (endpoint, p256dh, auth) tuple keyed by user
 * so it can deliver pushes when an event the user cares about fires.
 *
 * No-op when push is unsupported (Safari pre-16.4, embedded browsers,
 * incognito-in-some-browsers) or unconfigured server-side. Callers
 * should check ``isSupported()`` before showing a subscribe UI.
 */
import api from "./api/client";

export function isSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  // VAPID public keys are URL-safe base64; PushManager wants a Uint8Array.
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const safe = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(safe);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function getRegistration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration("/sw.js");
  if (existing) return existing;
  return navigator.serviceWorker.register("/sw.js");
}

export type SubscribeOutcome =
  | { status: "subscribed" }
  | { status: "denied" }
  | { status: "unsupported" }
  | { status: "not-configured" }
  | { status: "error"; error: string };

export async function subscribePush(): Promise<SubscribeOutcome> {
  if (!isSupported()) return { status: "unsupported" };

  try {
    const keyResp = await api.get("/api/v1/push/vapid-public-key");
    const publicKey = keyResp.data.public_key as string;
    if (!publicKey) return { status: "not-configured" };

    const permission = await Notification.requestPermission();
    if (permission !== "granted") return { status: "denied" };

    const registration = await getRegistration();
    // existing subscription wins — re-subscribe only swaps keys if
    // the browser has rotated them. PushManager.subscribe is
    // idempotent in practice but we don't want to spam the server.
    let sub = await registration.pushManager.getSubscription();
    if (!sub) {
      sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        // TS 5.x narrows Uint8Array generics in a way the PushManager
        // types don't accept yet; cast via BufferSource so the runtime
        // type (still a Uint8Array) lands without a type error.
        applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
      });
    }
    // Always POST so server-side endpoint URL stays current.
    const raw = sub.toJSON();
    await api.post("/api/v1/push/subscribe", {
      endpoint: raw.endpoint,
      keys: raw.keys,
    });
    return { status: "subscribed" };
  } catch (err: any) {
    return { status: "error", error: err?.message || String(err) };
  }
}

export async function unsubscribePush(): Promise<boolean> {
  if (!isSupported()) return false;
  try {
    const registration = await navigator.serviceWorker.getRegistration("/sw.js");
    if (!registration) return false;
    const sub = await registration.pushManager.getSubscription();
    if (!sub) return false;
    await api.post("/api/v1/push/unsubscribe", { endpoint: sub.endpoint });
    await sub.unsubscribe();
    return true;
  } catch {
    return false;
  }
}

export async function currentSubscription(): Promise<PushSubscriptionJSON | null> {
  if (!isSupported()) return null;
  const registration = await navigator.serviceWorker.getRegistration("/sw.js");
  if (!registration) return null;
  const sub = await registration.pushManager.getSubscription();
  return sub ? sub.toJSON() : null;
}

export async function testPush(): Promise<number> {
  const resp = await api.post("/api/v1/push/test");
  return resp.data.delivered_to as number;
}
